import { fetchEncryptionKey, clearEncryptionKey } from './encryptionManager.js';

const COMPANY_KEY = "amlguard_company";
const TEAM_KEY    = "amlguard_team";
const USER_KEY    = "amlguard_user";
const TOKEN_KEY   = "amlguard_token";

export function getCompany() {
  try { return JSON.parse(localStorage.getItem(COMPANY_KEY)); } catch { return null; }
}

export async function fetchCompanyFromDatabase(token, userId) {
  try {
    if (!token) return null;
    const response = await fetch(`/api/query?resource=companies&user_id=${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      const company = data?.company || data?.data?.[0] || data?.[0];
      if (company) {
        saveCompany(company);
        console.log('[Auth] Fetched company from database:', company.id, company.name);
        return company;
      }
    } else {
      const error = await response.json();
      console.warn('[Auth] Failed to fetch company:', error.error);
    }
  } catch (e) {
    console.warn('[Auth] Failed to fetch company from database:', e.message);
  }
  return null;
}
export async function saveCompany(company) {
  localStorage.setItem(COMPANY_KEY, JSON.stringify(company));

  // Also save to Neon database
  try {
    const token = localStorage.getItem('amlguard_token');
    if (!token || !company?.id) return;

    const response = await fetch('/api/db/companies', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: company.id,
        name: company.name,
        legal_name: company.legal_name,
        industry: company.industry,
        jurisdiction: company.jurisdiction,
      }),
    });

    if (response.ok) {
      console.log('[Auth] Company saved to Neon');
    } else {
      console.warn('[Auth] Failed to save company to Neon:', await response.text());
    }
  } catch (error) {
    console.warn('[Auth] Error saving company to Neon:', error.message);
  }
}
export function getTeam() {
  try { return JSON.parse(localStorage.getItem(TEAM_KEY) || "[]"); } catch { return []; }
}
export function saveTeam(team) {
  localStorage.setItem(TEAM_KEY, JSON.stringify(team));
}

export function getUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
}

export async function fetchUserFromDatabase(token, userId) {
  try {
    if (!token) return null;
    const response = await fetch(`/api/query?resource=users&id=${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      const user = data?.user || data?.data?.[0] || data?.[0];
      if (user) {
        saveUser(user);
        console.log('[Auth] Fetched user from database:', user.id, user.email, 'permission:', user.permission);
        return user;
      }
    } else {
      const error = await response.json();
      console.warn('[Auth] Failed to fetch user:', error.error);
    }
  } catch (e) {
    console.warn('[Auth] Failed to fetch user from database:', e.message);
  }
  return null;
}

export function saveUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export async function saveToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
  // Fetch encryption key from backend
  try {
    await fetchEncryptionKey(token);
  } catch (error) {
    console.warn('[Auth] Failed to fetch encryption key:', error);
    // Don't fail login if encryption key fetch fails
  }
}
export function clearAuth() {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  clearEncryptionKey();
}

export function getSession() {
  try { return JSON.parse(sessionStorage.getItem("amlguard_session")); } catch { return null; }
}
export function saveSession(user) {
  sessionStorage.setItem("amlguard_session", JSON.stringify(user));
}
export function clearSession() {
  sessionStorage.removeItem("amlguard_session");
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export async function registerUser({ firstName, lastName, email, password, companyName, inviteCode, isMasterAccount }) {
  if (!isValidEmail(email)) return { error: "Please enter a valid email address." };
  if (password.length < 8) return { error: "Password must be at least 8 characters." };

  try {
    console.log('[Auth] Registering user:', email);
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName, isMasterAccount }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('[Auth] Registration failed:', data.error);
      return { error: data.error || 'Registration failed' };
    }

    console.log('[Auth] Registration successful');
    saveUser(data.user);
    saveToken(data.token);
    saveSession(data.user);

    // If Master Account with company name, create company
    if (isMasterAccount && companyName) {
      const company = {
        id: data.user.id,
        name: companyName,
        created_at: new Date().toISOString(),
      };
      await saveCompany(company);
      console.log('[Auth] Created company:', company.id, 'with name:', companyName);
      return { user: data.user, company };
    }

    if (data.company) {
      await saveCompany(data.company);
      console.log('[Auth] Saved company:', data.company.id);
    }

    return { user: data.user, company: data.company };
  } catch (error) {
    console.error('[Auth] Registration exception:', error);
    return { error: error.message || 'Registration failed' };
  }
}

export async function loginUser({ email, password }) {
  if (!isValidEmail(email)) return { error: "Please enter a valid email address." };
  if (!password) return { error: "Password is required." };

  try {
    console.log('[Auth] Attempting login with:', email);
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    console.log('[Auth] Raw response from backend:', JSON.stringify(data));
    console.log('[Auth] Response status:', response.status, response.ok);

    if (!response.ok) {
      console.error('[Auth] Login failed:', data.error);
      return { error: data.error || 'Login failed' };
    }

    console.log('[Auth] User object keys:', Object.keys(data.user || {}));
    console.log('[Auth] User.permission:', data.user?.permission);
    console.log('[Auth] User.role:', data.user?.role);
    console.log('[Auth] User.isMasterAccount:', data.user?.isMasterAccount);
    console.log('[Auth] User.firstName:', data.user?.firstName);
    saveUser(data.user);
    saveToken(data.token);
    saveSession(data.user);
    if (data.company) {
      await saveCompany(data.company);
      console.log('[Auth] Saved company:', data.company.id);
    }

    return { user: data.user, company: data.company };
  } catch (error) {
    console.error('[Auth] Login exception:', error);
    return { error: error.message || 'Login failed' };
  }
}

export async function logoutUser() {
  console.log('[Auth] Logging out user');
  clearAuth();
  clearSession();
}

export async function getCurrentUser() {
  try {
    const token = getToken();
    if (!token) {
      return getUser(); // Return cached user if available
    }

    const response = await fetch('/api/auth/user', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('[Auth] Failed to get current user');
      clearAuth();
      return null;
    }

    const data = await response.json();
    saveUser(data.user);
    return data.user;
  } catch (error) {
    console.error('[Auth] Get current user exception:', error);
    // Return cached user if API fails
    return getUser();
  }
}

export async function verifyInviteCode(inviteCode) {
  try {
    const email = localStorage.getItem('signup_email') || '';
    const response = await fetch('/api/auth/verify-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inviteCode, email }),
    });

    const data = await response.json();
    return data.valid || false;
  } catch (error) {
    console.error('[Auth] Verify invite error:', error);
    return false;
  }
}

export async function generateInviteCode() {
  const token = getToken();
  if (!token) return { error: 'Not authenticated' };

  try {
    const response = await fetch('/api/auth/generate-invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ expirationDays: 7 }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { error: error.error || 'Failed to generate invite code' };
    }

    const data = await response.json();
    return { code: data.code, expiresAt: data.expiresAt };
  } catch (error) {
    return { error: error.message || 'Failed to generate invite code' };
  }
}

export function getUsers() {
  try { return JSON.parse(localStorage.getItem("amlguard_users") || "[]"); } catch { return []; }
}
export function saveUsers(users) {
  localStorage.setItem("amlguard_users", JSON.stringify(users));
}
export function userFromSupabase(supabaseUser) {
  if (!supabaseUser) return null;
  // Always create a fresh user object with admin permission
  // Don't use cached user from localStorage as it may have old permissions
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    firstName: supabaseUser.user_metadata?.first_name || supabaseUser.email.split("@")[0],
    lastName: supabaseUser.user_metadata?.last_name || "",
    role: "Compliance Officer",
    permission: "admin",
  };
}
